import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import {
  CHECK_SENDING_EMAIL_AVAILABLE_QUERY,
  DELETE_LOCATION_EMAIL_IDENTITY_MUTATION,
  LOCATION_EMAIL_IDENTITIES_QUERY,
  SENDING_DOMAINS_FOR_IDENTITY_QUERY,
  SET_LOCATION_EMAIL_IDENTITY_MUTATION,
  TRACKABLE_SENDING_DOMAIN_QUERY,
} from './graphql'
import { validateLocalPart } from './validateLocalPart'

type CustomDomain = ResultOf<typeof SENDING_DOMAINS_FOR_IDENTITY_QUERY>['sendingDomains']['hits'][number]
type Identity = ResultOf<typeof LOCATION_EMAIL_IDENTITIES_QUERY>['locationEmailIdentities']['hits'][number]

// Sentinel for the always-present shared Trackable option in the dropdown. A
// custom domain uses its SendingDomain id as the option value.
export const TRACKABLE_OPTION = `trackable`

export type DomainChoice = { value: string; label: string; domain: string }

type Availability = `idle` | `checking` | `available` | `taken`

export type UseLocationEmailIdentityReturn = {
  loading: boolean
  saving: boolean
  domainChoices: DomainChoice[]
  identity: Identity | undefined
  selectedValue: string
  setSelectedValue: (v: string) => void
  localPart: string
  setLocalPart: (v: string) => void
  fromName: string
  setFromName: (v: string) => void
  replyTo: string
  setReplyTo: (v: string) => void
  localPartError: string | undefined
  availability: Availability
  selectedDomainName: string | undefined
  previewAddress: string | undefined
  canSave: boolean
  onSave: () => Promise<void>
  onClear: () => Promise<void>
}

export function useLocationEmailIdentity(): UseLocationEmailIdentityReturn {
  const { locationId } = useParams()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([])
  const [trackableDomain, setTrackableDomain] = useState<string | undefined>(undefined)
  const [identity, setIdentity] = useState<Identity | undefined>(undefined)
  const [selectedValue, setSelectedValue] = useState(``)
  const [localPart, setLocalPart] = useState(``)
  const [fromName, setFromName] = useState(``)
  const [replyTo, setReplyTo] = useState(``)
  const [availability, setAvailability] = useState<Availability>(`idle`)

  const load = useCallback(async () => {
    if (!locationId) return
    setLoading(true)

    const [domainsResult, trackableResult, identityResult] = await Promise.all([
      gqlQuery(SENDING_DOMAINS_FOR_IDENTITY_QUERY, {}),
      gqlQuery(TRACKABLE_SENDING_DOMAIN_QUERY, {}),
      gqlQuery(LOCATION_EMAIL_IDENTITIES_QUERY, { search: `locationId.keyword:${locationId}` }),
    ])

    if (domainsResult.error) {
      notifyError(`Failed to load sending domains`)
      logError(`Failed to load sending domains`, domainsResult.error)
    }
    const verified = (domainsResult.data?.sendingDomains.hits ?? []).filter(
      (d) => d.status === `verified` && d.type === `custom`,
    )
    setCustomDomains(verified)
    const trackable = trackableResult.data?.trackableSendingDomain ?? undefined
    setTrackableDomain(trackable)

    const existing = identityResult.data?.locationEmailIdentities.hits[0]
    setIdentity(existing)
    if (existing) {
      // Map the saved identity back to a dropdown value: a custom domain by id,
      // or the Trackable sentinel when it matches the shared domain.
      setSelectedValue(existing.sendingDomainId ?? TRACKABLE_OPTION)
      setLocalPart(existing.localPart)
      setFromName(existing.fromName ?? ``)
      setReplyTo(existing.replyTo ?? ``)
    }

    setLoading(false)
  }, [locationId])

  useEffect(() => {
    load().catch((err) => logError(`Failed to load location email identity`, err))
  }, [load])

  const domainChoices = useMemo<DomainChoice[]>(() => {
    const choices: DomainChoice[] = []
    if (trackableDomain) {
      choices.push({ value: TRACKABLE_OPTION, label: `Trackable (${trackableDomain})`, domain: trackableDomain })
    }
    for (const d of customDomains) {
      choices.push({ value: d.id, label: d.domain, domain: d.domain })
    }
    return choices
  }, [trackableDomain, customDomains])

  const selectedDomainName = useMemo(
    () => domainChoices.find((c) => c.value === selectedValue)?.domain,
    [domainChoices, selectedValue],
  )

  const localPartError = useMemo(() => (localPart.trim() ? validateLocalPart(localPart) : undefined), [localPart])

  const previewAddress = useMemo(() => {
    if (!selectedDomainName || !localPart.trim() || localPartError) return undefined
    return `${localPart.trim().toLowerCase()}@${selectedDomainName}`
  }, [selectedDomainName, localPart, localPartError])

  // Live global availability check, debounced. Reruns whenever the resolved
  // address changes. The saved address for this location counts as available.
  const checkSeq = useRef(0)
  useEffect(() => {
    if (!selectedDomainName || !localPart.trim() || localPartError) {
      setAvailability(`idle`)
      return
    }
    const seq = ++checkSeq.current
    setAvailability(`checking`)
    const handle = setTimeout(() => {
      gqlQuery(CHECK_SENDING_EMAIL_AVAILABLE_QUERY, {
        localPart: localPart.trim().toLowerCase(),
        domain: selectedDomainName,
        locationId,
      })
        .then((result) => {
          if (seq !== checkSeq.current) return
          if (result.error) {
            setAvailability(`idle`)
            return
          }
          setAvailability(result.data?.checkSendingEmailAvailable ? `available` : `taken`)
        })
        .catch((err) => {
          if (seq === checkSeq.current) setAvailability(`idle`)
          logError(`availability check failed`, err)
        })
    }, 400)
    return () => clearTimeout(handle)
  }, [selectedDomainName, localPart, localPartError, locationId])

  const canSave = !!selectedValue && !!localPart.trim() && !localPartError && availability === `available` && !saving

  async function onSave(): Promise<void> {
    if (!locationId || !canSave) return
    setSaving(true)
    // Trackable option sends `domain`; a custom domain sends `sendingDomainId`.
    const isTrackable = selectedValue === TRACKABLE_OPTION
    const result = await gqlMutation(SET_LOCATION_EMAIL_IDENTITY_MUTATION, {
      input: {
        locationId,
        sendingDomainId: isTrackable ? undefined : selectedValue,
        domain: isTrackable ? selectedDomainName : undefined,
        localPart: localPart.trim().toLowerCase(),
        fromName: fromName.trim() || undefined,
        replyTo: replyTo.trim().toLowerCase() || undefined,
      },
    })
    setSaving(false)

    if (result.error) {
      notifyError(result.error.message || `Failed to save email identity`)
      return
    }
    notifySuccess(`Email identity saved`)
    await load()
  }

  async function onClear(): Promise<void> {
    if (!identity) return
    const result = await gqlMutation(DELETE_LOCATION_EMAIL_IDENTITY_MUTATION, { id: identity.id })
    if (result.error) {
      notifyError(result.error.message || `Failed to clear email identity`)
      return
    }
    notifySuccess(`Email identity cleared`)
    setIdentity(undefined)
    setSelectedValue(``)
    setLocalPart(``)
    setFromName(``)
    setReplyTo(``)
    setAvailability(`idle`)
    await load()
  }

  return {
    loading,
    saving,
    domainChoices,
    identity,
    selectedValue,
    setSelectedValue,
    localPart,
    setLocalPart,
    fromName,
    setFromName,
    replyTo,
    setReplyTo,
    localPartError,
    availability,
    selectedDomainName,
    previewAddress,
    canSave,
    onSave,
    onClear,
  }
}
